/**
 * PartnersHub — the /partners route for community-based care partners
 * (adult day programs, memory care communities, respite, in-home care,
 * caregiver support groups). Distinct from `/organizations`, which is
 * aimed at large healthcare systems, hospices, and GUIDE-model orgs.
 *
 * Deliberately compact: one hero, three connection points (get listed,
 * see the directory, refer a family), and the shared footer.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCountry } from "@/lib/country";
import SeoHead from "@/components/SeoHead";
import { LandingHeader, LandingFooter, QuickSignInModal } from "@/pages/Landing";
import {
    Users, ArrowRight, HeartHandshake, MapPin, ClipboardList, Building2, Send,
} from "lucide-react";

export default function PartnersHub() {
    const { t } = useCountry();
    const [signInOpen, setSignInOpen] = useState(false);

    return (
        <div className="min-h-screen bg-cream" id="top">
            <SeoHead
                title="For care partners — Memory Lane"
                description="Adult day programs, respite, memory care communities, home care, and caregiver support groups: get listed in Memory Lane's local resource directory and refer families to a caregiver-first navigation platform."
                canonicalPath="/partners"
            />

            <LandingHeader onOpenSignIn={() => setSignInOpen(true)} t={t} />

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-6" data-testid="partners-hub-hero">
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-dark">For care partners</p>
                <h1 className="mt-2 font-heading font-semibold tracking-tight leading-[1.05] text-3xl md:text-5xl text-[#12776F]">
                    Community-based partners who <span className="text-teal-dark">walk with families every day</span>.
                </h1>
                <p className="mt-4 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-3xl">
                    Adult day programs, memory-care communities, in-home care, respite, and caregiver support groups —
                    you&apos;re on the front line with families between doctor visits. Memory Lane helps caregivers
                    find you and gives you a way to send families home with continuity.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/partners/inquiry" data-testid="partners-hub-cta-inquiry" className="ml-btn-primary text-sm !py-2.5 !px-5">
                        <HeartHandshake size={15} strokeWidth={1.8} /> Get listed in Memory Lane
                    </Link>
                    <Link to="/care-network" data-testid="partners-hub-cta-directory" className="ml-btn-secondary text-sm !py-2.5 !px-5">
                        <MapPin size={15} strokeWidth={1.8} /> See the community directory
                    </Link>
                </div>
            </section>

            {/* Three ways to work with Memory Lane */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-14" data-testid="partners-hub-ways">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Three ways to work with us</p>
                <h2 className="mt-1 font-heading text-2xl md:text-3xl text-[#12776F]">Simple, no contracts required.</h2>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <WayCard
                        icon={Users}
                        title="Get discovered by local caregivers"
                        body="Caregivers search Memory Lane for adult day, respite, memory care, and home health near them. Free listing — you tell us who to send."
                        cta={{ to: "/partners/inquiry", label: "Send your details", testid: "way-listed" }}
                    />
                    <WayCard
                        icon={ClipboardList}
                        title="Give families a place to organize what changes"
                        body="When you meet a caregiver in crisis, send them to Memory Lane so their behaviors, questions, and services stay in one continuous place — not scattered across binders and texts."
                        cta={{ to: "/refer", label: "Refer a family", testid: "way-refer" }}
                    />
                    <WayCard
                        icon={Building2}
                        title="Larger health organization?"
                        body="If you're a hospice, health system, or GUIDE-model participant, see the pilot options built for organization-scale caregiver navigation."
                        cta={{ to: "/organizations", label: "See organization options", testid: "way-orgs" }}
                    />
                </div>
            </section>

            {/* Compact inquiry */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="partners-hub-cta-strip">
                <div className="ml-card bg-teal-soft/40 border border-teal-light p-6 md:p-8 text-center">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Have a program families should know about?</p>
                    <h2 className="mt-2 font-heading text-xl md:text-2xl text-[#12776F]">
                        Tell us what you do and where you serve — we&apos;ll add you.
                    </h2>
                    <div className="mt-4 flex flex-wrap justify-center gap-3">
                        <Link to="/partners/inquiry" data-testid="partners-hub-inquiry-btn" className="ml-btn-primary text-sm">
                            <Send size={14} strokeWidth={1.8} /> Send us your info <ArrowRight size={13} strokeWidth={2.4} />
                        </Link>
                    </div>
                </div>
            </section>

            <LandingFooter t={t} />

            <QuickSignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
        </div>
    );
}

function WayCard({ icon: Icon, title, body, cta }) {
    return (
        <div className="rounded-3xl border border-[#E2E1D8] bg-white p-5 md:p-6 h-full flex flex-col">
            <span className="w-10 h-10 rounded-2xl bg-[#EDF7F5] flex items-center justify-center mb-3">
                <Icon size={17} className="text-teal-dark" />
            </span>
            <h3 className="font-heading text-lg text-[#1F2624] leading-snug">{title}</h3>
            <p className="mt-2 text-sm text-[#4C5551] leading-relaxed flex-1">{body}</p>
            {cta && (
                <Link to={cta.to} data-testid={cta.testid} className="mt-4 text-sm font-semibold text-teal-dark hover:underline inline-flex items-center gap-1">
                    {cta.label} <ArrowRight size={12} strokeWidth={2.4} />
                </Link>
            )}
        </div>
    );
}
